import { useMemo, useState } from 'react';
import { useCurrentUserType } from './useCurrentUserType';
import { ApprovalStatus, ApprovalUserType, ApprovalStepStatus, ApprovalOperation, ApprovalTypes } from '../types';
import { useApprovalStepsInfo } from './useApprovalStepsInfo';
import { useCurrentUser } from './useCurrentUser';
import { useGetHasScope } from './useGetHasScope';
import { useHasApprovalScope } from './useHasApprovalScope';
import { canRejectApproval } from '../utils/canRejectApproval';
import { canRequestChanges } from '../utils/canRequestChanges';
import { useGetIsUngated } from './useGetIsUngated';
import { GATES } from '../constants/auth';
export const useApprovalMeta = ({
  approvalType,
  approvalAction,
  approvableObjectId,
  approvalObjectIds,
  assetName,
  approvalData,
  approvalConfig,
  restartOnConclude = false
}) => {
  const [currentOperation, setCurrentOperation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showApprovalEditPanel, setShowApprovalEditPanel] = useState(false);
  const currentUser = useCurrentUser();
  const hasScope = useGetHasScope();
  const isSuperAdmin = hasScope('super-admin');
  const hasApproveScope = useHasApprovalScope(approvalType, 'approve');
  const getIsUngated = useGetIsUngated();
  const hasChangesRequestedGate = getIsUngated(GATES.CHANGES_REQUESTED);
  const currentUserType = useCurrentUserType(approvalData.data, currentUser);
  const {
    stepCount,
    approvedCount,
    pendingCount,
    stepStatusForCurrentUser
  } = useApprovalStepsInfo(approvalData.data ? approvalData.data.approvalSteps : [], (currentUser === null || currentUser === void 0 ? void 0 : currentUser.user_id) != null ? String(currentUser.user_id) : undefined);
  const isLoading = useMemo(() => {
    return approvalData.isLoading || approvalConfig.isLoading;
  }, [approvalConfig, approvalData]);
  const availableOperations = useMemo(() => {
    if (!approvalConfig.config.enabled || isLoading) {
      return [];
    }
    const approvalStatus = approvalData.data ? approvalData.data.approvalStatus : undefined;
    switch (approvalStatus) {
      case ApprovalStatus.APPROVAL_PENDING:
        if (currentUserType === ApprovalUserType.APPROVER && stepStatusForCurrentUser === ApprovalStepStatus.APPROVAL_PENDING) {
          return [ApprovalOperation.APPROVE, ...(canRejectApproval({
            approvalType
          }) ? [ApprovalOperation.REJECT] : []), ...(hasChangesRequestedGate && canRequestChanges({
            approvalType
          }) ? [ApprovalOperation.REQUEST_CHANGES] : []), ...(hasApproveScope ? [ApprovalOperation.CANCEL] : [])];
        } else if (currentUserType === ApprovalUserType.REQUESTER) {
          return [ApprovalOperation.CANCEL];
        } else if (hasApproveScope) {
          return [ApprovalOperation.CANCEL];
        } else {
          return [];
        }
      case ApprovalStatus.CHANGES_REQUESTED:
        if (currentUserType === ApprovalUserType.APPROVER && (stepStatusForCurrentUser === ApprovalStepStatus.APPROVAL_PENDING || stepStatusForCurrentUser === ApprovalStepStatus.CHANGES_REQUESTED)) {
          return [ApprovalOperation.APPROVE, ...(canRejectApproval({
            approvalType
          }) ? [ApprovalOperation.REJECT] : []), ...(hasApproveScope ? [ApprovalOperation.CANCEL] : [])];
        } else if (currentUserType === ApprovalUserType.REQUESTER) {
          return [ApprovalOperation.CANCEL];
        } else if (hasApproveScope) {
          return [ApprovalOperation.CANCEL];
        } else {
          return [];
        }
      case undefined:
      case ApprovalStatus.CANCELLED:
        if (approvalType !== ApprovalTypes.QUOTE) {
          return [ApprovalOperation.REQUEST];
        }
        return [];
      case ApprovalStatus.APPROVED:
        if (restartOnConclude && approvalData.data && approvalData.data.approvalConcluded && approvalType !== ApprovalTypes.QUOTE) {
          return [ApprovalOperation.REQUEST];
        }
        return [];
      case ApprovalStatus.REJECTED:
        if (approvalType === ApprovalTypes.CONNECTED_APP) {
          return [ApprovalOperation.REQUEST];
        }
        return [];
      default:
        return [];
    }
  }, [approvalConfig.config.enabled, isLoading, approvalData.data, currentUserType, stepStatusForCurrentUser, hasApproveScope, hasChangesRequestedGate, approvalType, restartOnConclude]);

  // NOTE: We are moving away from approvalOperation in favour of 'availableOperations', which handles
  // multiple operations, e.g. APPROVE and REJECT.
  // TODO: update usage of approvalOperation to availableOperations in other repos, e.g ContentEditorUI.
  const approvalOperation = useMemo(() => {
    if (!approvalConfig.config.enabled || isLoading) {
      return null;
    }
    const approvalStatus = approvalData.data ? approvalData.data.approvalStatus : undefined;
    switch (approvalStatus) {
      case ApprovalStatus.APPROVAL_PENDING:
        if (currentUserType === ApprovalUserType.APPROVER && stepStatusForCurrentUser === ApprovalStepStatus.APPROVAL_PENDING) {
          return ApprovalOperation.APPROVE;
        } else if (currentUserType === ApprovalUserType.REQUESTER) {
          return ApprovalOperation.CANCEL;
        } else if (hasApproveScope) {
          return ApprovalOperation.CANCEL;
        } else {
          return null;
        }
      case ApprovalStatus.CHANGES_REQUESTED:
        if (currentUserType === ApprovalUserType.APPROVER && (stepStatusForCurrentUser === ApprovalStepStatus.APPROVAL_PENDING || stepStatusForCurrentUser === ApprovalStepStatus.CHANGES_REQUESTED)) {
          return ApprovalOperation.APPROVE;
        } else if (currentUserType === ApprovalUserType.REQUESTER) {
          return ApprovalOperation.CANCEL;
        } else if (hasApproveScope) {
          return ApprovalOperation.CANCEL;
        } else {
          return null;
        }
      case undefined:
      case ApprovalStatus.CANCELLED:
        if (approvalType !== ApprovalTypes.QUOTE) {
          return ApprovalOperation.REQUEST;
        }
        return null;
      case ApprovalStatus.APPROVED:
        if (restartOnConclude && approvalData.data && approvalData.data.approvalConcluded && approvalType !== ApprovalTypes.QUOTE) {
          return ApprovalOperation.REQUEST;
        }
        return null;
      case ApprovalStatus.REJECTED:
        if (approvalType === ApprovalTypes.CONNECTED_APP) {
          return ApprovalOperation.REQUEST;
        }
        return null;
      default:
        return null;
    }
  }, [approvalConfig, approvalData.data, approvalType, currentUserType, hasApproveScope, restartOnConclude, stepStatusForCurrentUser, isLoading]);
  const userCanSkipThisApproval = useMemo(() => {
    var _approvalConfig$confi, _approvalConfig$confi2;
    // No one can skip if the approval is pending.
    if (approvalData.data && approvalData.data.approvalStatus === ApprovalStatus.APPROVAL_PENDING) {
      return false;
    }
    // Super admins can skip when the approval is NOT pending.
    if (isSuperAdmin) {
      return true;
    }
    if (approvalConfig.config.skipApprovalOption) {
      // The user can skip if the skip strategy is 'EVERYONE_CAN_SKIP_APPROVAL'.
      switch (approvalConfig.config.skipApprovalOption) {
        case 'EVERYONE_CAN_SKIP_APPROVAL':
          return true;
        // The user can skip if the skip strategy is 'SPECIFIC_LIST_CAN_SKIP_APPROVAL' AND
        // their user ID is in the list.
        case 'SPECIFIC_LIST_CAN_SKIP_APPROVAL':
          return (_approvalConfig$confi = (_approvalConfig$confi2 = approvalConfig.config.usersIdsListThatCanSkipApproval) === null || _approvalConfig$confi2 === void 0 ? void 0 : _approvalConfig$confi2.includes((currentUser === null || currentUser === void 0 ? void 0 : currentUser.user_id) || 0)) !== null && _approvalConfig$confi !== void 0 ? _approvalConfig$confi : false;
        case 'NO_ONE_CAN_SKIP_APPROVAL':
        default:
          return false;
      }
    }
    return false;
  }, [approvalData, approvalConfig, currentUser, isSuperAdmin]);
  const httpErrors = {
    fetchData: approvalData.fetchError,
    hasFetchApproverUsersError: approvalData.hasFetchApproverUsersError
  };
  return {
    approvableObjectId,
    approvalObjectIds,
    approvalAction,
    approvalOperation,
    availableOperations,
    approvalType,
    approvedCount,
    assetName,
    currentUser,
    currentUserType,
    httpErrors,
    isLoading,
    isSuperAdmin,
    currentOperation,
    pendingCount,
    restartOnConclude,
    setCurrentOperation,
    setShowModal,
    setShowApprovalEditPanel,
    showApprovalEditPanel,
    showModal,
    stepCount,
    stepStatusForCurrentUser,
    userCanSkipThisApproval,
    approvalStatus: approvalData.data ? approvalData.data.approvalStatus : undefined
  };
};